/**
 * Reading a duel link, for display only.
 *
 * A share token is `base64url(payload).base64url(hmac)`. The payload is SIGNED,
 * NOT ENCRYPTED, so anyone holding the link can read what it is for — which is
 * exactly what the person who was sent it needs to see before they accept.
 *
 * NOTHING HERE IS TRUSTED. The gateway verifies the signature and resolves the
 * link itself; this only decides what to print on the invitation. If a forged
 * token claimed Cartographer, the worst it could do is mislabel a screen for
 * one tap, and the server would then run whatever the real claim says. That is
 * why there is no verification here and why there must never be: verifying
 * would need the secret, and the secret must never be in a bundle.
 *
 * The seed is deliberately absent from the payload for the same reason — see
 * `duelSeed` on the server. A token carries an opaque duel id, never the seed
 * it derives (invariant 5).
 */

import { isTier, type GameMode, type Tier } from '@geogame/shared';

export interface InviteFacts {
  readonly tier: Tier | null;
  readonly gameMode: GameMode;
  /** Seats the link opens a lobby for. 2 unless it says otherwise. */
  readonly capacity: number;
}

function isGameMode(v: unknown): v is GameMode {
  return v === 'country' || v === 'city' || v === 'state';
}

/**
 * What a link says about itself, or a country duel when it says nothing.
 *
 * Every field degrades on its own. A link minted before duels knew about game
 * modes carries no `gm` and IS a country duel; one minted before group links
 * carries no `cap` and IS a two-seat duel. Those defaults are the same ones the
 * gateway applies, so a stale link is described correctly rather than refused.
 */
export function readInvite(token: string): InviteFacts {
  const fallback: InviteFacts = { tier: null, gameMode: 'country', capacity: 2 };
  const dot = token.indexOf('.');
  if (dot <= 0) return fallback;

  let parsed: unknown;
  try {
    const body = token.slice(0, dot).replace(/-/g, '+').replace(/_/g, '/');
    parsed = JSON.parse(atob(body.padEnd(Math.ceil(body.length / 4) * 4, '=')));
  } catch {
    // An unreadable link is still worth offering: the server has the final say
    // and will refuse it with a message this screen can show.
    return fallback;
  }
  if (typeof parsed !== 'object' || parsed === null) return fallback;

  const p = parsed as Record<string, unknown>;
  const cap = typeof p['cap'] === 'number' && Number.isFinite(p['cap']) ? Math.trunc(p['cap']) : 2;
  return {
    tier: typeof p['tr'] === 'string' && isTier(p['tr']) ? p['tr'] : null,
    gameMode: isGameMode(p['gm']) ? p['gm'] : 'country',
    capacity: Math.max(2, Math.min(6, cap)),
  };
}
